/**
 * Thin adapters over browser APIs. Everything the player touches outside pure logic goes through
 * this object, so tests and the sample page can swap pieces (fakes live only in tests/ and demo/).
 */

/**
 * Inject a script from its original URL (async, main thread, never type="text/partytown", §15.4).
 * @param {Document} doc
 * @param {string} url
 * @param {object} [attrs]
 * @returns {Promise<void>}
 */
export function injectScript(doc, url, attrs = {}) {
  return new Promise((resolve, reject) => {
    const script = doc.createElement('script');
    script.async = true;
    script.src = url;
    for (const key of Object.keys(attrs)) script.setAttribute(key, attrs[key]);
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('script_load_failed'));
    doc.head.appendChild(script);
  });
}

/**
 * Storage accessor that never throws (private mode, blocked site data).
 * @param {Window} win
 * @param {'localStorage'|'sessionStorage'} name
 * @returns {Storage|null}
 */
function safeStorage(win, name) {
  try {
    const storage = win[name];
    storage.getItem('qsvp_probe');
    return storage;
  } catch (error) {
    return null;
  }
}

/**
 * Build the env for a real browser window.
 * @param {Window} win
 * @param {object} [overrides] replace any adapter (sample page mock mode)
 * @returns {object}
 */
export function createBrowserEnv(win, overrides = {}) {
  const doc = win.document;
  const current = doc.currentScript && doc.currentScript.src;
  const media = (query) => !!(win.matchMedia && win.matchMedia(query).matches);
  return Object.assign(
    {
      win,
      doc,
      now: () => Date.now(),
      random: () => Math.random(),
      setTimeout: (fn, ms) => win.setTimeout(fn, ms),
      clearTimeout: (handle) => win.clearTimeout(handle),
      setInterval: (fn, ms) => win.setInterval(fn, ms),
      requestFrame: (fn) => (win.requestAnimationFrame ? win.requestAnimationFrame(fn) : win.setTimeout(fn, 16)),
      IntersectionObserver: win.IntersectionObserver,
      ResizeObserver: win.ResizeObserver,
      MutationObserver: win.MutationObserver,
      fetch: win.fetch ? win.fetch.bind(win) : null,
      session: safeStorage(win, 'sessionStorage'),
      loadScript: (url, attrs) => injectScript(doc, url, attrs),
      isTrusted: (event) => !!(event && event.isTrusted),
      prefersReducedMotion: () => media('(prefers-reduced-motion: reduce)'),
      finePointer: () => media('(pointer: fine)'),
      // QA debug add-on served next to the player file (qs-video-player.debug.js).
      debugScriptUrl: current ? current.replace(/\.js(\?.*)?$/, '.debug.js') : '',
    },
    overrides,
  );
}
